/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
// @mui
import { styled } from '@mui/material/styles';
import {
  Card,
  Typography,
  Container,
  TextField,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Box,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { LoadingButton } from '@mui/lab';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import UploadIcon from '@mui/icons-material/Upload';
import { CSVLink } from 'react-csv';
import { Upload } from 'antd';
import { usePapaParse } from 'react-papaparse';
// components
import Page from '../../components/Page';
import instance from '../../api/axiosRequest';
import { clearEmailData, saveEmailData } from '../../features/campaign/campaignSlice';
import BackButton from '../../components/BackButton';
import openNotification from '../../components/notification';

// ----------------------------------------------------------------------

const ContentStyle = styled('div')(() => ({
  margin: 'auto',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
}));

// ----------------------------------------------------------------------

export default function CreateCampaign() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { email } = useSelector((state) => state.campaign);
  const { emailIdentities } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [verifiedEmails, setVerifiedEmails] = useState([]);
  const [emailVerified, setEmailverified] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const getTemplates = async () => {
      const { data } = await instance.get('/vendor/emailTemplates');
      setTemplates(data.data.rows);
    };
    getTemplates();
  }, []);

  useEffect(() => {
    if (emailIdentities && emailIdentities.length > 0) {
      setVerifiedEmails(emailIdentities);
      setEmailverified(true);
    }
  }, [emailIdentities]);

  useEffect(() => {
    if (email.edit) setEmailverified(true);
  }, [email]);

  useEffect(() => {
    if (email.to) {
      const toArray = email.to.map((e) => ({ name: e.name, email: e.email, contact: e.phone }));
      formik.setValues((values) => ({ ...values, to: toArray }));
    } else formik.setValues((values) => ({ ...values, to: [] }));
  }, [email.to]);

  const formik = useFormik({
    initialValues: {
      campain: email.campain || '',
      from: email.from || '',
      to: email.to ? email.to.map((e) => ({ name: e.name, email: e.email, contact: e.phone })) : [],
      template: email.template || '',
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        if (email.edit) await instance.put(`/vendor/emailCampain/${email.id}`, { id: email.id, ...values });
        else await instance.post('/vendor/emailCampain', values);
        setLoading(false);
        formik.handleReset();
        dispatch(clearEmailData());
        navigate('/dashboard/campaigns/campaigns');
      } catch (error) {
        setLoading(false);
        console.log(error);
      }
    },
  });

  useEffect(() => {
    dispatch(saveEmailData({ ...email, ...formik.values }));
  }, [formik.values.campain, formik.values.from, formik.values.template]);

  const handleEmailVerification = async () => {
    const emailToBeVerified = formik.values.from;
    if (!/\S+@\S+\.\S+/.test(emailToBeVerified)) {
      formik.setErrors({ from: 'Enter valid email address.' });
      return;
    }
    const emails = emailIdentities.map((e) => e.email);
    if (emails.includes(emailToBeVerified)) {
      openNotification({
        message: 'Email already verified.',
        description: 'Please enter a different email',
        type: 'error',
      });
      return;
    }
    try {
      setVerificationLoading(true);
      await instance.post('/mail/verifyEmailIdentity', { emailToBeVerified });
      setEmailverified(true);
      setVerificationLoading(false);
    } catch (error) {
      formik.setErrors({ from: 'Email not verified!' });
      setVerificationLoading(false);
      console.log(error);
    }
  };

  const handleReset = () => {
    dispatch(clearEmailData());
    formik.handleReset();
  };

  return (
    <Page title="Create Campaign">
      <Container>
        <BackButton />
        <ContentStyle>
          <Typography mb={2} variant="h4">
            Create Email Campaign
          </Typography>
          <Card sx={{ p: 2 }}>
            <form onSubmit={formik.handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  required
                  label="Campaign Name"
                  name="campain"
                  value={formik.values.campain}
                  onChange={formik.handleChange}
                />

                <Stack direction="row" spacing={2} alignItems="center">
                  {emailVerified ? (
                    <FormControl fullWidth>
                      <InputLabel id="from-select">From</InputLabel>
                      <Select
                        labelId="from-select"
                        id="from-simple-select"
                        value={formik.values.from}
                        label="From"
                        name="from"
                        onChange={formik.handleChange}
                      >
                        {verifiedEmails.map((t) => (
                          <MenuItem key={t.id} value={t.email}>
                            {t.email}
                          </MenuItem>
                        ))}
                      </Select>
                      <small
                        role="button"
                        style={{ textAlign: 'right', cursor: 'pointer', color: 'gray' }}
                        onClick={() => setEmailverified(false)}
                      >
                        <u>Use a different email</u>
                      </small>
                    </FormControl>
                  ) : (
                    <>
                      <TextField
                        name="from"
                        label="From"
                        type="email"
                        required
                        fullWidth
                        value={formik.values.from}
                        onChange={formik.handleChange}
                        sx={{ flexGrow: 1 }}
                        error={!!formik.errors.from}
                        helperText={formik.errors.from ? formik.errors.from : ''}
                      />
                      <LoadingButton
                        color="secondary"
                        loading={verificationLoading}
                        variant="outlined"
                        onClick={handleEmailVerification}
                        sx={{ width: 240, height: 56 }}
                      >
                        Verify Email
                      </LoadingButton>
                    </>
                  )}
                </Stack>
                <FormControl fullWidth>
                  <InputLabel id="template-select">Template Name</InputLabel>
                  <Select
                    labelId="template-select"
                    id="template-simple-select"
                    value={formik.values.template}
                    label="Template Name"
                    name="template"
                    onChange={formik.handleChange}
                  >
                    {templates.map((t) => (
                      <MenuItem key={t.id} value={t.template}>
                        {t.template}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {templates.length === 0 && (
                  <small>
                    No templates yet. Create one{' '}
                    <span
                      style={{ color: 'blue', cursor: 'pointer' }}
                      role="button"
                      onClick={() => navigate('/dashboard/campaigns/email-templates/custom')}
                    >
                      here
                    </span>
                  </small>
                )}
                <Stack direction="row" alignItems="center">
                  <Typography flexGrow={1}>Select Recipients:</Typography>
                  <Button
                    onClick={() => navigate('/dashboard/lead-center?hideHeader=1')}
                    color="secondary"
                    variant="outlined"
                    sx={{ mr: 2 }}
                  >
                    Leads
                  </Button>
                  <Button
                    startIcon={<FileUploadIcon />}
                    onClick={() => setOpen(true)}
                    color="secondary"
                    variant="outlined"
                  >
                    Upload
                  </Button>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography>Selected Recipients: {formik.values.to.length}</Typography>
                </Stack>
                <Stack direction="row" spacing={2} justifyContent="end">
                  <Button color="secondary" variant="contained" onClick={handleReset}>
                    Clear
                  </Button>
                  <LoadingButton variant="contained" loading={loading} disabled={!emailVerified} type="submit">
                    Submit
                  </LoadingButton>
                </Stack>
              </Stack>
            </form>
          </Card>
        </ContentStyle>
      </Container>
      <UploadTemplate open={open} setOpen={setOpen} />
    </Page>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  px: 8,
  py: 4,
};

function UploadTemplate({ open, setOpen }) {
  const { readRemoteFile } = usePapaParse();
  const dispatch = useDispatch();
  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const file = e.fileList[0].originFileObj;
    if (file) parseFile(file);
  };

  const parseFile = (file) => {
    readRemoteFile(file, {
      complete: (results) => {
        if (results.errors.length === 0) {
          const headers = results.data[0];
          const data = results.data.slice(1, results.data.length);
          const to = data.reduce((p, c) => {
            const obj = {};
            for (let i = 0; i < headers.length; i += 1) obj[headers[i]] = c[i];
            if (obj.email.length > 0) p.push(obj);
            return p;
          }, []);
          dispatch(saveEmailData({ to }));
          handleClose();
        } else {
          openNotification({
            message: 'Error reading file',
            description: 'Please download template for guidance.',
            type: 'error',
          });
        }
      },
    });
  };

  return (
    <div>
      <Modal open={open} onClose={handleClose} aria-labelledby="device-delete" aria-describedby="delete-device">
        <Box sx={style}>
          <Stack spacing={2} alignItems="center">
            <UploadIcon sx={{ fontSize: '5em' }} />
            <Typography variant="h5">Upload Leads</Typography>
            <Typography>
              Get excel file template to guide you on the format required{' '}
              <CSVLink data={[{ name: '', contact: '', email: '' }]} filename="template.csv">
                here
              </CSVLink>
              .
            </Typography>
            <Stack spacing={2} direction="row">
              <Button
                variant="contained"
                sx={{
                  backgroundColor: 'gray',
                  '&:hover': {
                    backgroundColor: 'gray',
                  },
                }}
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Upload onChange={handleChange}>
                <Button variant="contained">Upload</Button>
              </Upload>
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </div>
  );
}

UploadTemplate.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};
